import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Drawer, Input, Select, Table, Tooltip, message } from "antd";
import moment from "moment";
import DateRangePicker from "react-bootstrap-daterangepicker";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import { useLocation } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Clock,
  Copy,
  CreditCard,
  DollarSign,
  ExternalLink,
  FileText,
  Filter,
  Globe,
  RefreshCw,
  Search,
  Shield,
  Calendar,
  ChevronDown,
  X,
} from "react-feather";


const SYMBOLS = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ", THB: "฿" };
const money = (value, currency = "INR") => {
  const amount = Number(value || 0);
  const prefix = SYMBOLS[currency] || `${currency || ""} `;
  return `${prefix}${amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const dateTime = (value) => (value ? moment(value).format("DD MMM YYYY, hh:mm A") : "—");
const copy = (value) => {
  if (!value) return;
  navigator.clipboard?.writeText(String(value));
  message.success("Copied");
};

const STATUS = {
  calculated: { label: "Calculated", tone: "blue" },
  pending: { label: "Refund pending", tone: "blue" },
  processing: { label: "Processing", tone: "blue" },
  processed: { label: "Refunded", tone: "green" },
  manual: { label: "Manually refunded", tone: "green" },
  no_refund_required: { label: "No refund required", tone: "slate" },
  manual_required: { label: "Manual refund required", tone: "amber" },
  manual_review: { label: "Manual review", tone: "amber" },
  external_ota: { label: "OTA reconciliation", tone: "violet" },
  external_reconciled: { label: "OTA reconciled", tone: "green" },
  failed: { label: "Refund failed", tone: "red" },
};

const TONES = {
  blue: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", dot: "#3b82f6" },
  green: { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0", dot: "#10b981" },
  amber: { bg: "#fffbeb", color: "#92400e", border: "#fde68a", dot: "#f59e0b" },
  red: { bg: "#fef2f2", color: "#991b1b", border: "#fecaca", dot: "#ef4444" },
  violet: { bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe", dot: "#8b5cf6" },
  slate: { bg: "#f8fafc", color: "#475569", border: "#e2e8f0", dot: "#94a3b8" },
};

const statusMeta = (status) => STATUS[String(status || "").toLowerCase()] || { label: String(status || "Unknown").replace(/_/g, " "), tone: "slate" };

const Pill = ({ status, label }) => {
  const meta = statusMeta(status);
  const tone = TONES[meta.tone] || TONES.slate;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, background: tone.bg, color: tone.color, border: `1px solid ${tone.border}`, fontSize: 11, fontWeight: 800, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: tone.dot }} />
      {label || meta.label}
    </span>
  );
};

const StatCard = ({ icon, label, value, sub, active, onClick, warning }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: "100%",
      border: active ? "1px solid #93c5fd" : "1px solid #e5eaf0",
      background: active ? "#f7fbff" : "#fff",
      borderRadius: 16,
      padding: "15px 16px",
      textAlign: "left",
      boxShadow: active ? "0 8px 24px rgba(37,99,235,.08)" : "0 3px 12px rgba(15,23,42,.04)",
      cursor: onClick ? "pointer" : "default",
      minHeight: 105,
    }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <div>
        <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 800 }}>{label}</div>
        <div style={{ fontSize: 25, lineHeight: 1.1, color: warning ? "#b45309" : "#0f172a", fontWeight: 800, marginTop: 9 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>{sub}</div>}
      </div>
      <div style={{ width: 38, height: 38, borderRadius: 11, background: warning ? "#fffbeb" : "#f1f5f9", color: warning ? "#d97706" : "#475569", display: "grid", placeItems: "center", flexShrink: 0 }}>{icon}</div>
    </div>
  </button>
);

const DetailRow = ({ label, children, copyValue }) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 18, padding: "11px 0", borderBottom: "1px solid #eef2f7" }}>
    <div style={{ fontSize: 11, color: "#64748b", fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", minWidth: 142 }}>{label}</div>
    <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 650, textAlign: "right", wordBreak: "break-word" }}>
      {children || "—"}
      {copyValue && <button type="button" onClick={() => copy(copyValue)} style={{ background: "transparent", border: 0, padding: "0 0 0 5px", color: "#94a3b8", cursor: "pointer" }}><Copy size={11} /></button>}
    </div>
  </div>
);

const SectionTitle = ({ icon, children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "20px 0 7px", paddingBottom: 9, borderBottom: "2px solid #eef2f7", color: "#475569", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: ".09em" }}>
    {icon}{children}
  </div>
);

const originLabel = (row) => {
  if (row?.is_ota) return row.pms_booking_source || row.booking_source || "OTA";
  if (row?.be_booking_id) return "Booking Engine";
  return "StayX Direct";
};

const ownerLabel = (row) => {
  const owner = String(row?.refund_owner || "property").toLowerCase();
  if (owner === "ota") return "OTA";
  if (owner === "gateway") return "Gateway";
  if (owner === "property") return "Property";
  return String(row?.refund_owner || "Property").replace(/_/g, " ");
};

const RefundReconciliation = () => {
  const location = useLocation();
  const isStayX = location.pathname.startsWith("/hospitality");
  const apiURL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const [range, setRange] = useState([moment().subtract(90, "days").startOf("day"), moment().endOf("day")]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(undefined);
  const [origin, setOrigin] = useState(undefined);
  const [owner, setOwner] = useState(undefined);
  const [attention, setAttention] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(null);

  const endpoint = isStayX ? "/booking/refunds" : "/booking-engine/refunds";

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        startDate: range[0].format("YYYY-MM-DD"),
        endDate: range[1].format("YYYY-MM-DD"),
        page: String(page),
        pageSize: String(pageSize),
      });
      if (search.trim()) params.set("search", search.trim());
      if (status) params.set("status", status);
      if (origin) params.set("origin", origin);
      if (owner) params.set("owner", owner);
      if (attention) params.set("attention", "true");

      const response = await fetch(`${apiURL}${endpoint}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) throw new Error(data.message || "Unable to load refunds.");
      setRows(data.refunds || []);
      setStats(data.stats || {});
      setTotal(Number(data.pagination?.total || 0));
    } catch (err) {
      setError(err.message || "Unable to load refund records.");
    } finally {
      setLoading(false);
    }
  }, [apiURL, endpoint, token, range, page, pageSize, search, status, origin, owner, attention]);

  useEffect(() => { load(); }, [load]);

  const handleDateChange = (start, end) => {
    const nextStart = moment(start).startOf("day");
    const nextEnd = moment(end).endOf("day");
    if (!nextStart.isValid() || !nextEnd.isValid() || nextStart.isAfter(nextEnd)) return;
    setRange([nextStart, nextEnd]);
    setPage(1);
  };

  const resetFilters = () => {
    setSearch("");
    setStatus(undefined);
    setOrigin(undefined);
    setOwner(undefined);
    setAttention(false);
    setPage(1);
    setRange([moment().subtract(90, "days").startOf("day"), moment().endOf("day")]);
  };

  const statsCurrency = Number(stats.currency_count || 0) === 1 ? (stats.currency || rows[0]?.currency || "INR") : null;
  const actualRefundedStat = statsCurrency ? money(stats.actual_refunded_total, statsCurrency) : (Number(stats.currency_count || 0) > 1 ? "Mixed currency" : money(0, "INR"));
  const eligibleRefundedSub = statsCurrency ? `Eligible ${money(stats.eligible_total, statsCurrency)}` : (Number(stats.currency_count || 0) > 1 ? "Use currency-specific filters for totals" : "Eligible ₹0.00");
  const statusOptions = useMemo(() => Object.entries(STATUS).map(([value, meta]) => ({ value, label: meta.label })), []);

  const columns = [
    {
      title: "Refund case",
      dataIndex: "refund_number",
      width: 170,
      fixed: "left",
      render: (value, row) => (
        <button type="button" onClick={() => setSelected(row)} style={{ background: "transparent", border: 0, padding: 0, textAlign: "left", cursor: "pointer" }}>
          <div style={{ fontFamily: "monospace", color: "#1d4ed8", fontSize: 13, fontWeight: 800 }}>{value}</div>
          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>{dateTime(row.initiated_at)}</div>
        </button>
      ),
    },
    {
      title: "Booking / guest",
      width: 250,
      render: (_, row) => (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>{row.guest_name || "Guest"}</span>
            {row.is_ota && <Globe size={12} color="#7c3aed" />}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>
            {row.booking_engine_number || row.stayx_booking_number || row.booking_source_id || "—"}
          </div>
          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{originLabel(row)}</div>
        </div>
      ),
    },
    {
      title: "Financial position",
      width: 255,
      render: (_, row) => (
        <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.65 }}>
          <div><span style={{ display: "inline-block", minWidth: 78 }}>Collected</span><strong style={{ color: "#0f172a" }}>{money(row.amount_paid, row.currency)}</strong></div>
          <div><span style={{ display: "inline-block", minWidth: 78 }}>Eligible</span><strong style={{ color: "#0f172a" }}>{row.eligible_refund_amount == null ? "—" : money(row.eligible_refund_amount, row.currency)}</strong></div>
          <div><span style={{ display: "inline-block", minWidth: 78 }}>Refunded</span><strong style={{ color: Number(row.actual_refund_amount || 0) > 0 ? "#047857" : "#475569" }}>{row.actual_refund_amount == null ? "—" : money(row.actual_refund_amount, row.currency)}</strong></div>
        </div>
      ),
    },
    {
      title: "Ownership",
      width: 170,
      render: (_, row) => (
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a" }}>{ownerLabel(row)}</div>
          <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "capitalize", marginTop: 3 }}>{String(row.execution_mode || "manual").replace(/_/g, " ")}</div>
          {(row.payment_gateway || row.payment_method) && <div style={{ fontSize: 10, color: "#64748b", marginTop: 3 }}>{row.payment_gateway || row.payment_method}</div>}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "refund_status",
      width: 185,
      render: (value, row) => (
        <div>
          <Pill status={value} />
          {row.needs_attention && <div style={{ display: "flex", alignItems: "center", gap: 5, color: "#b45309", fontSize: 10, marginTop: 6 }}><AlertTriangle size={11} />{row.attention_reason || "Needs attention"}</div>}
        </div>
      ),
    },
    {
      title: "Updated",
      dataIndex: "updated_at",
      width: 160,
      render: (value) => <span style={{ fontSize: 11, color: "#64748b" }}>{dateTime(value)}</span>,
    },
    {
      title: "",
      width: 70,
      fixed: "right",
      render: (_, row) => <Button size="small" onClick={() => setSelected(row)} style={{ borderRadius: 8 }}><ExternalLink size={13} /></Button>,
    },
  ];

  return (
    <div className={`refund-workspace${isStayX ? " page-wrapper" : ""}`}>
      <style>{`
        .refund-workspace{padding:18px 18px 28px;min-height:100vh;background:#f6f8fb}
        .refund-title{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;margin-bottom:16px}
        .refund-stats{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:12px;margin-bottom:14px}
        .refund-filters{display:grid;grid-template-columns:minmax(220px,1.3fr) 290px 185px 175px 165px auto;gap:10px;align-items:center;background:#fff;border:1px solid #e6ebf1;border-radius:16px;padding:12px;margin-bottom:14px;box-shadow:0 3px 12px rgba(15,23,42,.035)}
        .refund-date-range{width:100%;height:38px;display:flex;align-items:center;border:1px solid #d9e0e8;border-radius:9px;background:#fff;overflow:hidden;cursor:pointer;transition:border-color .15s,box-shadow .15s}
        .refund-date-range:hover{border-color:#94b8ff;box-shadow:0 0 0 2px rgba(37,99,235,.04)}
        .refund-date-range-icon{width:38px;display:grid;place-items:center;color:#64748b;flex:0 0 38px}
        .refund-date-range input{border:0!important;outline:0!important;box-shadow:none!important;background:transparent!important;height:36px!important;padding:0 2px!important;font-size:12px!important;font-weight:700!important;color:#172033!important;text-align:center;cursor:pointer;min-width:0}
        .refund-date-range-chevron{width:32px;display:grid;place-items:center;color:#64748b;flex:0 0 32px}
        .daterangepicker{font-family:inherit!important;border:1px solid #e2e8f0!important;border-radius:12px!important;box-shadow:0 16px 42px rgba(15,23,42,.16)!important}
        .daterangepicker td.active,.daterangepicker td.active:hover{background:#2563eb!important}
        .daterangepicker .ranges li.active{background:#eff6ff!important;color:#1d4ed8!important}
        .daterangepicker .applyBtn{background:#2563eb!important;border-color:#2563eb!important}
        .refund-table-wrap{background:#fff;border:1px solid #e6ebf1;border-radius:16px;overflow:hidden;box-shadow:0 4px 16px rgba(15,23,42,.04)}
        .refund-workspace .ant-table-thead>tr>th{background:#f8fafc!important;color:#475569!important;font-size:11px!important;font-weight:800!important;text-transform:uppercase;letter-spacing:.055em;border-bottom:1px solid #e5eaf0!important}
        .refund-workspace .ant-table-tbody>tr>td{border-bottom:1px solid #eef2f7!important;padding-top:13px!important;padding-bottom:13px!important}
        .refund-workspace .ant-table-tbody>tr:hover>td{background:#fbfdff!important}
        @media(max-width:1200px){.refund-stats{grid-template-columns:repeat(3,minmax(0,1fr))}.refund-filters{grid-template-columns:1fr 1fr 1fr}.refund-filters .wide{grid-column:span 2}}
        @media(max-width:760px){.refund-workspace{padding:12px}.refund-title{flex-direction:column}.refund-stats{grid-template-columns:1fr 1fr}.refund-filters{grid-template-columns:1fr}.refund-filters .wide{grid-column:auto}}
      `}</style>

      <div className="refund-title">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 9, color: "#2563eb", fontSize: 11, textTransform: "uppercase", letterSpacing: ".11em", fontWeight: 900 }}><Shield size={15} /> Central financial control</div>
          <h2 style={{ margin: "7px 0 4px", fontSize: 25, color: "#0f172a", fontWeight: 800 }}>Refund & Reconciliation</h2>
        </div>
        <Button onClick={load} loading={loading} icon={<RefreshCw size={14} />} style={{ height: 38, borderRadius: 10, fontWeight: 700 }}>Refresh</Button>
      </div>

      <div className="refund-stats">
        <StatCard icon={<FileText size={18} />} label="Refund cases" value={Number(stats.total_cases || 0).toLocaleString()} sub="Within current filters" />
        <StatCard icon={<AlertTriangle size={18} />} label="Needs attention" value={Number(stats.needs_attention || 0).toLocaleString()} sub="Manual, failed, OTA or ledger" warning active={attention} onClick={() => { setAttention((v) => !v); setPage(1); }} />
        <StatCard icon={<CheckCircle size={18} />} label="Completed" value={Number(stats.completed_cases || 0).toLocaleString()} sub="Processed / reconciled" />
        <StatCard icon={<Clock size={18} />} label="In progress" value={Number(stats.in_progress_cases || 0).toLocaleString()} sub="Calculated / pending" />
        <StatCard icon={<DollarSign size={18} />} label="Actual refunded" value={actualRefundedStat} sub={eligibleRefundedSub} />
      </div>

      <div className="refund-filters">
        <Input className="wide" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} allowClear prefix={<Search size={14} color="#94a3b8" />} placeholder="Search refund, booking, guest, email or phone" style={{ height: 38, borderRadius: 9 }} />
        <DateRangePicker
          initialSettings={{
            singleDatePicker: false,
            showDropdowns: true,
            startDate: range[0].toDate(),
            endDate: range[1].toDate(),
            ranges: {
              Today: [moment().startOf("day").toDate(), moment().endOf("day").toDate()],
              "This Month": [moment().startOf("month").toDate(), moment().endOf("month").toDate()],
              "Last Month": [moment().subtract(1, "month").startOf("month").toDate(), moment().subtract(1, "month").endOf("month").toDate()],
              "Last 90 Days": [moment().subtract(90, "days").startOf("day").toDate(), moment().endOf("day").toDate()],
              "This Year": [moment().startOf("year").toDate(), moment().endOf("day").toDate()],
            },
            locale: { format: "DD MMM YYYY", applyLabel: "Select", cancelLabel: "Cancel" },
          }}
          onCallback={(start, end) => handleDateChange(start, end)}
        >
          <div className="refund-date-range" title="Select refund date range">
            <span className="refund-date-range-icon"><Calendar size={14} /></span>
            <input
              type="text"
              className="form-control"
              readOnly
              value={`${range[0].format("DD MMM YYYY")} – ${range[1].format("DD MMM YYYY")}`}
              aria-label="Refund date range"
            />
            <span className="refund-date-range-chevron"><ChevronDown size={14} /></span>
          </div>
        </DateRangePicker>
        <Select allowClear value={status} onChange={(v) => { setStatus(v); setPage(1); }} placeholder="Refund status" options={statusOptions} style={{ width: "100%" }} />
        <Select allowClear value={origin} onChange={(v) => { setOrigin(v); setPage(1); }} placeholder="Booking origin" options={[{ value: "booking_engine", label: "Booking Engine" }, { value: "stayx", label: "StayX direct" }, { value: "ota", label: "OTA" }]} style={{ width: "100%" }} />
        <Select allowClear value={owner} onChange={(v) => { setOwner(v); setPage(1); }} placeholder="Refund owner" options={[{ value: "gateway", label: "Gateway" }, { value: "property", label: "Property" }, { value: "ota", label: "OTA" }]} style={{ width: "100%" }} />
        <Tooltip title="Clear filters"><Button onClick={resetFilters} style={{ height: 38, borderRadius: 9 }}><Filter size={14} /></Button></Tooltip>
      </div>

      {error && <div style={{ display: "flex", gap: 8, alignItems: "center", background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: 12, padding: "10px 13px", marginBottom: 12, fontSize: 12 }}><AlertCircle size={15} />{error}</div>}

      <div className="refund-table-wrap">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 1350 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50, 100],
            showTotal: (count) => `${count.toLocaleString()} refund cases`,
            onChange: (nextPage, nextSize) => { setPage(nextPage); setPageSize(nextSize); },
          }}
        />
      </div>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        width={540}
        zIndex={12000}
        closeIcon={
          <span
            aria-label="Close refund details"
            title="Close"
            style={{
              width: 32,
              height: 32,
              borderRadius: 9,
              display: "inline-grid",
              placeItems: "center",
              background: "#f8fafc",
              border: "1px solid #dbe3ec",
              color: "#334155",
              boxShadow: "0 2px 8px rgba(15,23,42,.06)",
            }}
          >
            <X size={18} strokeWidth={2.2} />
          </span>
        }
        destroyOnClose
        bodyStyle={{ padding: "0 24px 28px" }}
        title={selected ? (
          <div>
            <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: ".08em", fontWeight: 800 }}>Refund case</div>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 4 }}><span style={{ fontFamily: "monospace", fontSize: 17, fontWeight: 900, color: "#0f172a" }}>{selected.refund_number}</span><Pill status={selected.refund_status} /></div>
          </div>
        ) : null}
      >
        {selected && (
          <>
            {selected.needs_attention && (
              <div style={{ display: "flex", gap: 10, background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", padding: "12px 13px", borderRadius: 12, marginTop: 18, fontSize: 12, lineHeight: 1.5 }}>
                <AlertTriangle size={17} style={{ flexShrink: 0, marginTop: 1 }} />
                <div><strong>{selected.attention_reason || "Needs attention"}</strong><br />{selected.notes || "This refund case requires an operational follow-up before it can be considered fully reconciled."}</div>
              </div>
            )}

            <SectionTitle icon={<CreditCard size={14} />}>Financial position</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, margin: "10px 0 4px" }}>
              {[['Collected', selected.amount_paid], ['Eligible', selected.eligible_refund_amount], ['Refunded', selected.actual_refund_amount]].map(([label, value]) => (
                <div key={label} style={{ background: "#f8fafc", border: "1px solid #e7ecf2", borderRadius: 11, padding: "11px 10px" }}>
                  <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".07em" }}>{label}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: label === 'Refunded' && Number(value || 0) > 0 ? "#047857" : "#0f172a", marginTop: 5 }}>{value == null ? "—" : money(value, selected.currency)}</div>
                </div>
              ))}
            </div>
            <DetailRow label="Refund owner">{ownerLabel(selected)}</DetailRow>
            <DetailRow label="Execution">{String(selected.execution_mode || "manual").replace(/_/g, " ")}</DetailRow>
            <DetailRow label="Payment method">{selected.payment_method || "—"}</DetailRow>
            <DetailRow label="Payment gateway">{selected.payment_gateway || "—"}</DetailRow>
            <DetailRow label="Original payment ref" copyValue={selected.original_payment_reference}>{selected.original_payment_reference || "—"}</DetailRow>
            <DetailRow label="Gateway refund ID" copyValue={selected.gateway_refund_id}>{selected.gateway_refund_id || "—"}</DetailRow>

            <SectionTitle icon={<FileText size={14} />}>Reservation links</SectionTitle>
            <DetailRow label="Origin">{originLabel(selected)}</DetailRow>
            <DetailRow label="Guest">{selected.guest_name || "Guest"}</DetailRow>
            <DetailRow label="Booking Engine" copyValue={selected.booking_engine_number}>{selected.booking_engine_number || "—"}</DetailRow>
            <DetailRow label="StayX reservation" copyValue={selected.stayx_booking_number}>{selected.stayx_booking_number || "—"}</DetailRow>
            {selected.booking_source_id && <DetailRow label="External reference" copyValue={selected.booking_source_id}>{selected.booking_source_id}</DetailRow>}
            <DetailRow label="BE lifecycle">{selected.be_booking_status || "—"}</DetailRow>
            <DetailRow label="StayX lifecycle">{selected.stayx_booking_status || "—"}</DetailRow>

            <SectionTitle icon={<Clock size={14} />}>Audit trail</SectionTitle>
            <DetailRow label="Initiated from">{String(selected.initiated_from || "—").replace(/_/g, " ")}</DetailRow>
            <DetailRow label="Initiated">{dateTime(selected.initiated_at)}</DetailRow>
            <DetailRow label="Processed">{dateTime(selected.processed_at)}</DetailRow>
            <DetailRow label="Last updated">{dateTime(selected.updated_at)}</DetailRow>
            {selected.notes && <div style={{ marginTop: 13, padding: 12, background: "#f8fafc", border: "1px solid #e7ecf2", borderRadius: 11, color: "#475569", fontSize: 12, lineHeight: 1.55 }}>{selected.notes}</div>}
          </>
        )}
      </Drawer>
    </div>
  );
};

export default RefundReconciliation;
